import { Injectable } from '@angular/core';
import { Business, BusinessAddress } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
} from '@models/matter';
import { format } from 'date-fns';
import { addressString, getRegionName } from '@core/util/address';
import {
	clientLegalName,
	getSignatories,
	isGuarantor,
} from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { isSet } from '@core/util/value';
import { GetDateObject } from '@core/util/datetime';
import { getPrimaryParcel } from '@core/util/parcels';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { joinNames } from '@core/util/string';

@Injectable({
	providedIn: 'root',
})
export class FIRST_NATIONAL_REQUEST_FOR_FUNDS_Mapper extends BaseFormMapper<FIRST_NATIONAL_REQUEST_FOR_FUNDS> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: FIRST_NATIONAL_REQUEST_FOR_FUNDS,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): FIRST_NATIONAL_REQUEST_FOR_FUNDS {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as FIRST_NATIONAL_REQUEST_FOR_FUNDS);

		if (matter?.lawyerAssigned && !isSet(_formData?.lawyerName)) {
			_formData.lawyerName = matter.lawyerAssigned.name;
		}

		if (businessAddress?.street && !isSet(_formData?.addressStreet1)) {
			_formData.addressStreet1 = businessAddress.street;
		}

		if (businessAddress?.street2 && !isSet(_formData?.addressStreet2)) {
			_formData.addressStreet2 = businessAddress.street2;
		}

		if (
			(businessAddress?.city || businessAddress?.province) &&
			!isSet(_formData?.addressCityAndProvince)
		) {
			_formData.addressCityAndProvince = `${businessAddress?.city}, ${getRegionName(businessAddress?.province, businessAddress?.country)}`;
		}

		if (businessAddress?.postcode && !isSet(_formData?.addressPostCode)) {
			_formData.addressPostCode = businessAddress.postcode;
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.mortgageNo)) {
			_formData.mortgageNo = mortgage.referenceNumber;
		}

		if (matterClients?.length && !isSet(_formData?.mortgagors)) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);
			const Contacts = getSignatories(FilteredMatterClients);
			_formData.mortgagors = joinNames(
				Contacts.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.guarantors && !isSet(_formData?.guarantorsOrCocovenantors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.guarantorsOrCocovenantors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		const PrimaryTitle = getPrimaryParcel(matterTitles);
		if (PrimaryTitle?.description && !isSet(_formData?.legalDescription)) {
			_formData.legalDescription = PrimaryTitle.description;
		}

		if (
			matter?.propertyDetails?.address &&
			!isSet(_formData?.municipalAddressIncludingPostalCode)
		) {
			_formData.municipalAddressIncludingPostalCode = addressString(
				matter.propertyDetails.address,
			);
		}

		if (
			matter?.propertyDetails?.lro?.selected &&
			!isSet(_formData?.registrationDistrict)
		) {
			_formData.registrationDistrict = matter.propertyDetails.lro.selected.name;
		}

		if (
			matter?.titleInsurance?.companyName &&
			!isSet(_formData?.nameOfTitleInsuranceCompany)
		) {
			_formData.nameOfTitleInsuranceCompany = matter.titleInsurance.companyName;
		}

		if (!isSet(_formData?.checkBox28)) {
			_formData.checkBox28 = 'Yes';
		}

		if (
			matter?.propertyDetails?.rollNumber &&
			!isSet(_formData?.taxRollNumber)
		) {
			_formData.taxRollNumber = matter.propertyDetails.rollNumber;
		}

		if (
			businessAddress &&
			!isSet(_formData?.accountHolderBeneficiaryPhysicalAddress)
		) {
			const address = businessAddress;
			_formData.accountHolderBeneficiaryPhysicalAddress =
				addressString(address);
		}

		if (matter?.closingDate && !isSet(_formData?.closingDateDateOfWire)) {
			_formData.closingDateDateOfWire = format(
				GetDateObject(matter.closingDate),
				'MMMM dd, yyyy',
			);
		}

		if (!isSet(_formData?.date)) {
			_formData.date = format(new Date(), 'MMMM dd, yyyy');
		}

		if (matter?.lawyerAssigned && !isSet(_formData?.lawyerNameBottom)) {
			_formData.lawyerNameBottom = matter.lawyerAssigned?.name;
		}

		if (business?.name && !isSet(_formData?.businessNameBottom)) {
			_formData.businessNameBottom = business.name;
		}

		return _formData;
	}

	MapValues(): Partial<FIRST_NATIONAL_REQUEST_FOR_FUNDS> {
		return {};
	}
}

const Mappings: FIRST_NATIONAL_REQUEST_FOR_FUNDS = {
	lawyerName: 'Lawyer Name',
	addressStreet1: 'Address Street1',
	addressStreet2: 'Address Street2',
	addressCityAndProvince: 'Address City and province',
	addressPostCode: 'Address Post code',

	mortgageNo: 'Mortgage No',
	mortgagors: 'Mortgagors',
	guarantorsOrCocovenantors: 'Guarantors or CoCovenantors',
	spouseOfMortgagor: 'Spouse of Mortgagor',
	legalDescription: 'Legal Description',
	municipalAddressIncludingPostalCode:
		'Municipal Address including postal code',
	registrationDistrict: 'Registration District',

	nameOfTitleInsuranceCompany: 'Name of Title Insurance Company',
	policyNo: 'Policy No',
	checkBox28: 'Check Box28',
	checkBox29: 'Check Box29',

	taxRollNumber: 'Tax Roll Number',
	municipalTaxOfficephoneNo: 'Municipal Tax OfficePhone No',
	currentYearGrossTaxes: 'Current Year Gross Taxes',
	amountPaidToDate: 'Amount Paid To Date',
	amountUnpaid: 'Amount Unpaid',
	theAmountInArrears: 'The amount in arrears',

	trustAccNumber: 'Trust Acc Number',
	transitNumber: 'Transit Number',
	bankNumber: 'Bank Number',
	accountHolderBeneficiaryName: 'AccountholderBeneficiary Name',
	accountHolderBeneficiaryPhysicalAddress:
		'AccountholderBeneficiary Physical Address',
	closingDateDateOfWire: 'Closing Date  Date of  Wire',
	date: 'Date',
	solicitorsSignature: 'Solicitors Signature',
	lawyerNameBottom: 'Lawyer Name Bottom',
	businessNameBottom: 'Business Name Bottom',
};

export interface FIRST_NATIONAL_REQUEST_FOR_FUNDS {
	lawyerName: string;
	addressStreet1: string;
	addressStreet2: string;
	addressCityAndProvince: string;
	addressPostCode: string;

	mortgagors: string;
	guarantorsOrCocovenantors: string;
	spouseOfMortgagor: string;
	legalDescription: string;
	municipalAddressIncludingPostalCode: string;
	registrationDistrict: string;

	nameOfTitleInsuranceCompany: string;
	policyNo: string;
	mortgageNo: string;
	checkBox28: string;
	checkBox29: string;

	taxRollNumber: string;
	municipalTaxOfficephoneNo: string;
	currentYearGrossTaxes: string;
	amountPaidToDate: string;
	amountUnpaid: string;
	theAmountInArrears: string;

	trustAccNumber: string;
	transitNumber: string;
	bankNumber: string;
	accountHolderBeneficiaryName: string;
	accountHolderBeneficiaryPhysicalAddress: string;
	closingDateDateOfWire: string;
	date: string;

	solicitorsSignature: string;
	lawyerNameBottom: string;
	businessNameBottom: string;
}
